import Text from 'components/atomic/atoms/text';
import { typography } from 'design-system/tokens';
import { validateVariant } from 'ui/utils';
import { usePropertyType } from './use-property-type';

export const PROPERTY_TYPE_VARIANTS = {
  HERO: 'hero',
  CARD: 'card',
} as const;

export type PropertyTypeVariant = typeof PROPERTY_TYPE_VARIANTS[keyof typeof PROPERTY_TYPE_VARIANTS];

const VARIANT_CONFIG = {
  [PROPERTY_TYPE_VARIANTS.HERO]: {
    textVariant: 'body2' as const,
    component: 'h2' as const,
    // Same 4px step down as hero address (headingLarge → headingMedium).
    fontSize: {
      xs: typography.fontSize.xs,
      sm: typography.fontSize.sm,
      md: typography.fontSize.md,
      lg: typography.fontSize.lg,
      xl: typography.fontSize.lgPlus,
    },
    lineHeight: typography.lineHeight.base,
    color: 'text.secondary',
  },
  [PROPERTY_TYPE_VARIANTS.CARD]: {
    textVariant: 'h4' as const,
    component: 'h4' as const,
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.base,
    color: 'text.secondary',
  },
};

export interface PropertyTypeProps {
  propertyType: string | null;
  variant: PropertyTypeVariant;
}

/**
 * PropertyType UI component
 * 
 * Displays the property type (e.g., "Detached House", "Condo Apartment").
 * Variant is required - no default assumed.
 */
export function PropertyType({
  propertyType,
  variant,
}: PropertyTypeProps) {
  const { formatted, hasValue } = usePropertyType({ propertyType });

  if (!hasValue) {
    return null;
  }

  if (!validateVariant('PropertyType', variant, PROPERTY_TYPE_VARIANTS)) {
    return null;
  }

  const config = VARIANT_CONFIG[variant];

  return (
    <Text
      variant={config.textVariant}
      component={config.component}
      sx={{
        fontSize: config.fontSize,
        lineHeight: config.lineHeight,
        color: config.color,
      }}
      data-testid="ui-property-type"
    >
      {formatted}
    </Text>
  );
}
